import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../App";
import { stateMatches } from "../utils/state";

export interface SiteRecord {
  siteId?: string;
  locationCode: string;
  oldLocationCode: string;
  state: string;
}

export function buildUniqueLocationOptions(values: string[]) {
  const seen = new Set<string>();
  return values
    .map((value) => value?.trim())
    .filter((value): value is string => {
      if (!value || seen.has(value)) return false;
      seen.add(value);
      return true;
    })
    .map((value) => ({ value, label: value }));
}

export function useSiteLocationOptions(
  authToken: string,
  accountId: string,
  selectedState: string
) {
  const [sites, setSites] = useState<SiteRecord[]>([]);
  const [loadingSites, setLoadingSites] = useState(false);

  const fetchSites = useCallback(async () => {
    if (!authToken || !accountId) return;

    setLoadingSites(true);
    try {
      const pageSize = 500;
      const headers = { Authorization: `Bearer ${authToken}` };
      const firstResponse = await axios.get(
        `${API_BASE_URL}account/${accountId}/sites?page=1&limit=${pageSize}`,
        { headers }
      );
      const totalCount = firstResponse.data.totalCount || 0;
      let allSites: SiteRecord[] = [...(firstResponse.data.sites || [])];
      let currentPage = 2;

      while (allSites.length < totalCount) {
        const response = await axios.get(
          `${API_BASE_URL}account/${accountId}/sites?page=${currentPage}&limit=${pageSize}`,
          { headers }
        );
        allSites = [...allSites, ...(response.data.sites || [])];
        currentPage++;
      }

      setSites(allSites);
    } catch (error) {
      console.error("Error fetching sites:", error);
    } finally {
      setLoadingSites(false);
    }
  }, [authToken, accountId]);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  const sitesForState = useMemo(
    () => sites.filter((site) => stateMatches(site.state, selectedState)),
    [sites, selectedState]
  );

  const newLocationCodeOptions = useMemo(
    () => buildUniqueLocationOptions(sitesForState.map((site) => site.locationCode)),
    [sitesForState]
  );

  const oldLocationCodeOptions = useMemo(
    () => buildUniqueLocationOptions(sitesForState.map((site) => site.oldLocationCode)),
    [sitesForState]
  );

  const findSiteByLocationCode = useCallback(
    (locationCode: string) =>
      sitesForState.find((site) => site.locationCode === locationCode),
    [sitesForState]
  );

  const findSiteByOldLocationCode = useCallback(
    (oldLocationCode: string) =>
      sitesForState.find((site) => site.oldLocationCode === oldLocationCode),
    [sitesForState]
  );

  return {
    sites,
    loadingSites,
    fetchSites,
    sitesForState,
    newLocationCodeOptions,
    oldLocationCodeOptions,
    findSiteByLocationCode,
    findSiteByOldLocationCode,
  };
}
